/**
 * Linear Interpolation Utilities
 * 
 * These functions handle the smooth momentum-based playback system.
 */

/**
 * Linear interpolation between two values
 * 
 * @param start - Starting value
 * @param end - Target value
 * @param factor - Interpolation factor (0-1)
 * @returns Interpolated value
 * 
 * @example
 * lerp(0, 100, 0.5) // Returns 50
 * lerp(10, 20, 0.1) // Returns 11
 */
export function lerp(start: number, end: number, factor: number): number {
    return start + (end - start) * factor;
}

/**
 * Clamp a value between min and max
 * 
 * @param value - Value to clamp
 * @param min - Minimum value
 * @param max - Maximum value
 * @returns Clamped value
 */
export function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

/**
 * Wrap a value within a range (for looping)
 * 
 * @param value - Value to wrap
 * @param min - Minimum value
 * @param max - Maximum value (exclusive)
 * @returns Wrapped value
 * 
 * @example
 * wrap(5, 0, 10) // Returns 5
 * wrap(12, 0, 10) // Returns 2
 * wrap(-1, 0, 10) // Returns 9
 */
export function wrap(value: number, min: number, max: number): number {
    const range = max - min;
    const wrapped = ((value - min) % range + range) % range + min;
    return wrapped;
}

/**
 * Calculate the nearest integer frame from a floating-point frame value
 * 
 * @param frame - Floating-point frame number
 * @returns Integer frame number
 */
export function getFrameIndex(frame: number): number {
    return Math.floor(frame);
}

/**
 * Check if we've crossed a specific frame threshold
 * Useful for detecting when to trigger audio events
 * 
 * @param prevFrame - Previous frame number
 * @param currentFrame - Current frame number
 * @param targetFrame - Frame to check if we crossed
 * @returns True if we crossed the target frame
 * 
 * @example
 * // Moving forward
 * crossedFrame(44, 46, 45) // Returns true
 * 
 * // Moving backward
 * crossedFrame(46, 44, 45) // Returns true
 * 
 * // Didn't cross
 * crossedFrame(43, 44, 45) // Returns false
 */
export function crossedFrame(
    prevFrame: number,
    currentFrame: number,
    targetFrame: number
): boolean {
    const prevInt = getFrameIndex(prevFrame);
    const currInt = getFrameIndex(currentFrame);

    // Moving forward
    if (currInt > prevInt) {
        return prevInt < targetFrame && currInt >= targetFrame;
    }

    // Moving backward
    if (currInt < prevInt) {
        return prevInt >= targetFrame && currInt < targetFrame;
    }

    return false;
}
